// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { Ban, Plus, Trash2, Clock, User, AlertCircle, Search } from 'lucide-react'
import { getBlockList, unblockNumber, quickBlockNumber } from '../utils/blocklistHelper'

export default function BlockListPage() {
  const [blocks, setBlocks] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadBlockList()
  }, [])

  const loadBlockList = async () => {
    try {
      setLoading(true)
      const result = await getBlockList(100)
      setBlocks(result.blocks)
    } catch (error) {
      console.error('ブロックリスト取得エラー:', error)
      alert('ブロックリストの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveBlock = async (blockId, phoneNumber) => {
    if (!confirm(`${phoneNumber} のブロックを解除しますか？`)) return

    try {
      await unblockNumber(blockId)
      setBlocks(blocks.filter(b => b.blockId !== blockId))
      alert('ブロックを解除しました')
    } catch (error) {
      console.error('ブロック解除エラー:', error)
      alert('ブロック解除に失敗しました')
    }
  }

  const filteredBlocks = blocks.filter(block =>
    block.phoneNumber.includes(searchQuery) ||
    block.reason.includes(searchQuery) ||
    (block.notes && block.notes.includes(searchQuery))
  )

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            着信拒否リスト
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            ブロック中: {blocks.length}件
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          ブロック追加
        </button>
      </div>

      {/* 検索バー */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="電話番号、理由、メモで検索..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">読み込み中...</p>
        </div>
      ) : filteredBlocks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Ban className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery ? '検索結果がありません' : 'ブロックリストは空です'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBlocks.map(block => (
            <BlockCard
              key={block.blockId}
              block={block}
              onRemove={handleRemoveBlock}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddBlockModal
          onClose={() => setShowAddModal(false)}
          onAdd={loadBlockList}
        />
      )}
    </div>
  )
}

function BlockCard({ block, onRemove }) {
  const isExpiring = block.expiresAt && block.expiresAt < Date.now() + 86400000 // 24時間以内

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Ban className="w-5 h-5 text-red-600" />
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              {block.phoneNumber}
            </span>
            {block.blockType === 'auto' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                自動
              </span>
            )}
          </div>

          <p className="text-gray-600 dark:text-gray-400 mb-2">
            理由: {block.reason}
          </p>

          {block.notes && (
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-2 bg-gray-50 dark:bg-gray-700 p-2 rounded">
              メモ: {block.notes}
            </p>
          )}

          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {block.blockedByName}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(block.blockedAt).toLocaleString('ja-JP')}
            </div>
          </div>

          {block.expiresAt && (
            <div className={`flex items-center gap-1 text-sm mt-2 ${
              isExpiring ? 'text-orange-600' : 'text-gray-500 dark:text-gray-400'
            }`}>
              <AlertCircle className="w-4 h-4" />
              期限: {new Date(block.expiresAt).toLocaleString('ja-JP')}
              {isExpiring && ' (まもなく期限切れ)'}
            </div>
          )}
        </div>

        <button
          onClick={() => onRemove(block.blockId, block.phoneNumber)}
          className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center gap-1 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          解除
        </button>
      </div>
    </div>
  )
}

function AddBlockModal({ onClose, onAdd }) {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    reason: '',
    notes: '',
    hasExpiry: false,
    expiryDays: 30,
  })
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setSubmitting(true)

      const expiresAt = formData.hasExpiry
        ? Date.now() + (formData.expiryDays * 86400000)
        : null

      await quickBlockNumber(
        formData.phoneNumber,
        formData.reason,
        formData.notes,
        expiresAt
      )

      alert('ブロックリストに追加しました')
      onAdd()
      onClose()
    } catch (error) {
      console.error('ブロック追加エラー:', error)
      const errorMessage = error.response?.data?.error || 'ブロック追加に失敗しました'
      alert(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
          ブロックリストに追加
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              電話番号 *
            </label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              placeholder="+81-90-1234-5678"
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              ブロック理由 *
            </label>
            <select
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">選択してください</option>
              <option value="迷惑電話">迷惑電話</option>
              <option value="営業電話">営業電話</option>
              <option value="スパム">スパム</option>
              <option value="嫌がらせ">嫌がらせ</option>
              <option value="その他">その他</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              メモ（オプション）
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="ブロックの詳細や経緯を記録..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hasExpiry}
                onChange={(e) => setFormData({ ...formData, hasExpiry: e.target.checked })}
                className="rounded text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">期限を設定する</span>
            </label>

            {formData.hasExpiry && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  value={formData.expiryDays}
                  onChange={(e) => setFormData({ ...formData, expiryDays: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">日後に自動解除</span>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 transition-colors"
            >
              {submitting ? '追加中...' : '追加'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
